import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Download, Send, CheckCircle2, Trash2, FileText, Shield, FileCode, Bell, ThumbsUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { invoicesAPI, zugferdAPI, emailAPI, downloadFile } from '../../services/api';
import { useCompany } from '../../services/companyState';
import { unitLabel, formatEuro } from '../../services/serviceCatalog';
import LoadingSpinner from '../Common/LoadingSpinner';
import './InvoiceDetail.css';

const STATUS_LABELS = {
  draft: 'Entwurf',
  sent: 'Versendet',
  paid: 'Bezahlt',
  overdue: 'Überfällig',
};
const STATUS_CLASS = {
  draft: 'status-badge status-draft',
  sent: 'status-badge status-sent',
  paid: 'status-badge status-paid',
  overdue: 'status-badge status-overdue',
};

const formatDE = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const company = useCompany();
  const isKleinunternehmer = company?.small_business === true;
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await invoicesAPI.getById(id);
      setInvoice(data);
    } catch (err) {
      toast.error(err.response?.status === 404 ? 'Rechnung nicht gefunden' : 'Fehler beim Laden');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDownloadPdf = async () => {
    try {
      setBusy(true);
      if (!invoice.pdf_path) {
        await invoicesAPI.generatePDF(id);
      }
      const res = await invoicesAPI.download(id);
      downloadFile(res.data, `Rechnung_${invoice.invoice_number}.pdf`);
    } catch (err) {
      toast.error('Fehler beim PDF-Download');
    } finally {
      setBusy(false);
    }
  };

  const handleGeneratePdf = async () => {
    try {
      setBusy(true);
      await invoicesAPI.generatePDF(id);
      toast.success('PDF erstellt');
      load();
    } catch (err) {
      toast.error('PDF-Erstellung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!window.confirm('Rechnung als bezahlt markieren?')) return;
    try {
      setBusy(true);
      await invoicesAPI.markPaid(id);
      toast.success('Als bezahlt markiert');
      load();
    } catch (err) {
      toast.error('Fehler');
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    if (!invoice.customer?.email) {
      toast.error('Kunde hat keine E-Mail-Adresse');
      return;
    }
    if (!window.confirm(`Rechnung an ${invoice.customer.email} senden?`)) return;
    try {
      setBusy(true);
      await invoicesAPI.send(id);
      toast.success('Rechnung wird versendet');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Versenden');
    } finally {
      setBusy(false);
    }
  };

  const handleReminder = async (type) => {
    const labels = { friendly: 'Freundliche Erinnerung', urgent: '2. Mahnung', final: 'Letzte Mahnung' };
    if (!window.confirm(`${labels[type]} an ${invoice.customer?.email} senden?`)) return;
    try {
      setBusy(true);
      await emailAPI.sendReminder(id, type);
      toast.success(`${labels[type]} versendet`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!window.confirm(`Zahlungsbestätigung an ${invoice.customer?.email} senden?`)) return;
    try {
      setBusy(true);
      await emailAPI.sendConfirmation(id);
      toast.success('Zahlungsbestätigung versendet');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Rechnung ${invoice.invoice_number} wirklich löschen?`)) return;
    try {
      setBusy(true);
      await invoicesAPI.delete(id);
      toast.success('Gelöscht');
      navigate('/invoices');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="large" />
      </div>
    );
  }
  if (!invoice) return null;

  const items = (invoice.items || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));

  return (
    <div>
      <div className="content-header">
        <div>
          <Link to="/invoices" className="back-link">
            <ArrowLeft size={14} />
            Zurück zur Übersicht
          </Link>
          <h1>
            Rechnung {invoice.invoice_number}{' '}
            <span className={STATUS_CLASS[invoice.status]} style={{ fontSize: '0.7em', verticalAlign: 'middle', marginLeft: '0.5rem' }}>
              {STATUS_LABELS[invoice.status] || invoice.status}
            </span>
          </h1>
          {invoice.title && <p>{invoice.title}</p>}
        </div>
        <div className="flex gap-2">
          {invoice.status === 'draft' && (
            <Link to={`/invoices/${id}/edit`} className="btn btn-outline">
              <Edit2 size={14} />
              Bearbeiten
            </Link>
          )}
          <button onClick={handleDownloadPdf} className="btn btn-outline" disabled={busy}>
            <Download size={14} />
            PDF
          </button>
          {!invoice.pdf_path && (
            <button onClick={handleGeneratePdf} className="btn btn-outline" disabled={busy}>
              <FileText size={14} />
              PDF erzeugen
            </button>
          )}
          {invoice.status === 'draft' && invoice.customer?.email && (
            <button onClick={handleSend} className="btn btn-primary" disabled={busy}>
              <Send size={14} />
              Versenden
            </button>
          )}
          {invoice.status !== 'paid' && (
            <button onClick={handleMarkPaid} className="btn btn-primary" disabled={busy}>
              <CheckCircle2 size={14} />
              Bezahlt
            </button>
          )}
          {(invoice.status === 'sent' || invoice.status === 'overdue') && invoice.customer?.email && (
            <div className="dropdown-wrapper">
              <button className="btn btn-outline" disabled={busy} onClick={() => handleReminder('friendly')}>
                <Bell size={14} />
                Mahnung
              </button>
            </div>
          )}
          {invoice.status === 'paid' && invoice.customer?.email && (
            <button onClick={handleConfirmPayment} className="btn btn-outline" disabled={busy}>
              <ThumbsUp size={14} />
              Bestätigung
            </button>
          )}
          {invoice.status === 'draft' && (
            <button onClick={handleDelete} className="btn btn-danger" disabled={busy}>
              <Trash2 size={14} />
              Löschen
            </button>
          )}
        </div>
      </div>

      <div className="content-body">
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="card">
            <div className="card-header"><h2>Rechnungsempfänger</h2></div>
            <div className="card-body">
              <div className="font-semibold">{invoice.customer?.name}</div>
              <div>{invoice.customer?.address_line1}</div>
              {invoice.customer?.address_line2 && <div>{invoice.customer.address_line2}</div>}
              <div>{invoice.customer?.postal_code} {invoice.customer?.city}</div>
              <div>{invoice.customer?.country}</div>
              {invoice.customer?.email && (
                <div className="text-sm text-gray-600 mt-2">{invoice.customer.email}</div>
              )}
            </div>
          </div>
          <div className="card">
            <div className="card-header"><h2>Rechnungsdaten</h2></div>
            <div className="card-body">
              <dl className="invoice-meta">
                <dt>Rechnungsnummer</dt><dd>{invoice.invoice_number}</dd>
                <dt>Rechnungsdatum</dt><dd>{formatDE(invoice.invoice_date)}</dd>
                <dt>Leistungsdatum</dt><dd>{formatDE(invoice.service_date)}</dd>
                <dt>Fälligkeit</dt><dd>{formatDE(invoice.due_date)}</dd>
                {invoice.sent_at && (<><dt>Versendet am</dt><dd>{new Date(invoice.sent_at).toLocaleString('de-DE')}</dd></>)}
                {invoice.paid_at && (<><dt>Bezahlt am</dt><dd>{new Date(invoice.paid_at).toLocaleString('de-DE')}</dd></>)}
              </dl>
            </div>
          </div>
        </div>

        <div className="card mb-6">
          <div className="card-header"><h2>Positionen</h2></div>
          <div className="card-body">
            <table className="table">
              <thead>
                <tr>
                  <th>Pos.</th>
                  <th>Beschreibung</th>
                  <th className="text-right">Menge</th>
                  <th>Einheit</th>
                  <th className="text-right">Einzelpreis</th>
                  <th className="text-right">Gesamt</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={it.id}>
                    <td>{idx + 1}</td>
                    <td>{it.description}</td>
                    <td className="text-right">{Number(it.quantity).toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                    <td>{unitLabel(it.unit)}</td>
                    <td className="text-right">{formatEuro(it.unit_price)}</td>
                    <td className="text-right font-semibold">{formatEuro(it.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} className="text-right font-semibold">Nettobetrag</td>
                  <td className="text-right font-semibold">{formatEuro(invoice.net_amount)}</td>
                </tr>
                {isKleinunternehmer ? (
                  <tr>
                    <td colSpan={5} className="text-right">Umsatzsteuer (§19 UStG)</td>
                    <td className="text-right">0,00 €</td>
                  </tr>
                ) : (
                  Object.entries(invoice.vat_breakdown || {}).length > 0 ? (
                    Object.entries(invoice.vat_breakdown).map(([rate, amounts]) => (
                      <tr key={rate}>
                        <td colSpan={5} className="text-right">
                          Umsatzsteuer {Number(rate).toFixed(0)}% (auf {formatEuro(amounts.net)})
                        </td>
                        <td className="text-right">{formatEuro(amounts.vat)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-right">Umsatzsteuer</td>
                      <td className="text-right">{formatEuro(invoice.vat_amount || 0)}</td>
                    </tr>
                  )
                )}
                <tr>
                  <td colSpan={5} className="text-right font-semibold" style={{ fontSize: '1.05rem' }}>Rechnungsbetrag</td>
                  <td className="text-right font-semibold" style={{ fontSize: '1.05rem', color: '#1d4ed8' }}>
                    {formatEuro(isKleinunternehmer ? invoice.net_amount : (invoice.gross_amount || invoice.net_amount))}
                  </td>
                </tr>
              </tfoot>
            </table>
            {isKleinunternehmer && (
              <div className="kleinunternehmer-notice" style={{ marginTop: '0.75rem' }}>
                Gemäß §19 UStG (Kleinunternehmer) wird keine Umsatzsteuer berechnet.
              </div>
            )}
          </div>
        </div>

        {invoice.notes && (
          <div className="card mb-6">
            <div className="card-header"><h2>Anmerkungen</h2></div>
            <div className="card-body" style={{ whiteSpace: 'pre-wrap' }}>{invoice.notes}</div>
          </div>
        )}

        {/* ZUGFeRD Section */}
        <EInvoiceCard invoice={invoice} onUpdate={load} />
      </div>
    </div>
  );
};

const EInvoiceCard = ({ invoice, onUpdate }) => {
  const [generatingZf, setGeneratingZf] = useState(false);
  const [generatingXr, setGeneratingXr] = useState(false);
  const [validatingZf, setValidatingZf] = useState(false);
  const [validatingXr, setValidatingXr] = useState(false);
  const [validationZf, setValidationZf] = useState(null);
  const [validationXr, setValidationXr] = useState(null);

  // ZUGFeRD handlers
  const handleGenerateZf = async () => {
    try {
      setGeneratingZf(true);
      const { data } = await zugferdAPI.generate(invoice.id);
      toast.success(data.xml_valid
        ? `ZUGFeRD ${data.profile} PDF erstellt`
        : `ZUGFeRD PDF erstellt (${data.validation_errors.length} Warnung(en))`);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'ZUGFeRD-Erstellung fehlgeschlagen');
    } finally { setGeneratingZf(false); }
  };

  const handleValidateZf = async () => {
    try {
      setValidatingZf(true);
      const { data } = await zugferdAPI.validate(invoice.id);
      setValidationZf(data);
      toast[data.valid ? 'success' : 'error'](data.valid ? 'ZUGFeRD CII XML ist valide' : `${data.errors.length} Validierungsfehler`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Validierung fehlgeschlagen');
    } finally { setValidatingZf(false); }
  };

  const handleDownloadZfXml = async () => {
    try {
      const res = await zugferdAPI.downloadXml(invoice.id);
      downloadFile(res.data, `${invoice.invoice_number}_zugferd.xml`);
    } catch (err) { toast.error('XML-Download fehlgeschlagen'); }
  };

  // XRechnung handlers
  const handleGenerateXr = async () => {
    try {
      setGeneratingXr(true);
      const { data } = await zugferdAPI.generateXRechnung(invoice.id);
      toast.success(data.xml_valid
        ? 'XRechnung UBL 2.1 XML erstellt'
        : `XRechnung erstellt (${data.validation_errors.length} Warnung(en))`);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'XRechnung-Erstellung fehlgeschlagen');
    } finally { setGeneratingXr(false); }
  };

  const handleValidateXr = async () => {
    try {
      setValidatingXr(true);
      const { data } = await zugferdAPI.validateXRechnung(invoice.id);
      setValidationXr(data);
      toast[data.valid ? 'success' : 'error'](data.valid ? 'XRechnung UBL ist valide' : `${data.errors.length} Validierungsfehler`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Validierung fehlgeschlagen');
    } finally { setValidatingXr(false); }
  };

  const handleDownloadXrXml = async () => {
    try {
      const res = await zugferdAPI.downloadXRechnungXml(invoice.id);
      downloadFile(res.data, `${invoice.invoice_number}_xrechnung.xml`);
    } catch (err) { toast.error('XML-Download fehlgeschlagen'); }
  };

  const ValidationBlock = ({ validation, label }) => {
    if (!validation) return null;
    return (
      <div className={`zugferd-validation mt-3 ${validation.valid ? 'valid' : 'invalid'}`}>
        <strong>{validation.valid ? 'Valide' : 'Fehler gefunden'}</strong>
        <span> — {label}, {validation.xml_size_bytes} Bytes</span>
        {validation.errors?.length > 0 && (
          <ul className="validation-errors">
            {validation.errors.map((err, i) => <li key={i}>{err}</li>)}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2>
          <Shield size={18} style={{ display: 'inline', verticalAlign: 'text-bottom', marginRight: '0.4rem' }} />
          E-Rechnung
        </h2>
      </div>
      <div className="card-body">
        {/* ── ZUGFeRD Section ── */}
        <div className="einvoice-section">
          <div className="einvoice-section-header">
            <div>
              <strong>ZUGFeRD / Factur-X</strong>
              <span className="text-muted" style={{ fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                CII XML in PDF/A-3 — B2B Standard
              </span>
            </div>
            <div className="flex gap-2">
              <button onClick={handleGenerateZf} className="btn btn-primary btn-sm" disabled={generatingZf}>
                <FileText size={14} />
                {generatingZf ? 'Generiere...' : invoice.zugferd_profile ? 'Neu generieren' : 'Erzeugen'}
              </button>
              <button onClick={handleValidateZf} className="btn btn-outline btn-sm" disabled={validatingZf}>
                <Shield size={14} /> Prüfen
              </button>
              {invoice.zugferd_xml_path && (
                <button onClick={handleDownloadZfXml} className="btn btn-outline btn-sm">
                  <FileCode size={14} /> CII XML
                </button>
              )}
            </div>
          </div>
          {invoice.zugferd_profile ? (
            <div className="einvoice-status">
              <span className="zugferd-badge zugferd-active">ZUGFeRD {invoice.zugferd_profile}</span>
              <span className="text-muted" style={{ fontSize: '0.82rem' }}>
                PDF/A-3 mit eingebettetem CII XML — EN 16931
              </span>
            </div>
          ) : (
            <p className="text-muted" style={{ fontSize: '0.85rem', margin: '0.5rem 0 0' }}>
              Noch nicht generiert. Für B2B-Rechnungen empfohlen.
            </p>
          )}
          <ValidationBlock validation={validationZf} label={`Profil: ${validationZf?.profile || '—'}`} />
        </div>

        <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '1rem 0' }} />

        {/* ── XRechnung Section ── */}
        <div className="einvoice-section">
          <div className="einvoice-section-header">
            <div>
              <strong>XRechnung</strong>
              <span className="text-muted" style={{ fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                UBL 2.1 XML — Pflicht für B2G (Behörden)
              </span>
            </div>
            <div className="flex gap-2">
              <button onClick={handleGenerateXr} className="btn btn-primary btn-sm" disabled={generatingXr}>
                <FileText size={14} />
                {generatingXr ? 'Generiere...' : invoice.xrechnung_xml_path ? 'Neu generieren' : 'Erzeugen'}
              </button>
              <button onClick={handleValidateXr} className="btn btn-outline btn-sm" disabled={validatingXr}>
                <Shield size={14} /> Prüfen
              </button>
              {invoice.xrechnung_xml_path && (
                <button onClick={handleDownloadXrXml} className="btn btn-outline btn-sm">
                  <FileCode size={14} /> UBL XML
                </button>
              )}
            </div>
          </div>
          {invoice.xrechnung_xml_path ? (
            <div className="einvoice-status">
              <span className="zugferd-badge" style={{ background: '#dbeafe', color: '#1d4ed8' }}>XRechnung 3.0</span>
              <span className="text-muted" style={{ fontSize: '0.82rem' }}>
                UBL 2.1 XML — Peppol BIS Billing 3.0
              </span>
            </div>
          ) : (
            <p className="text-muted" style={{ fontSize: '0.85rem', margin: '0.5rem 0 0' }}>
              Noch nicht generiert. Pflicht für Rechnungen an Behörden (Leitweg-ID).
            </p>
          )}
          <ValidationBlock validation={validationXr} label={validationXr?.format || 'XRechnung'} />
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetail;
